import { Box, Checkbox } from "@mui/material";
import type { AnswerComponent } from "api";
import React, { useRef } from "react";
import { TextBlocksRenderer } from "../TextBlocksRenderer";
import {
  Controller,
  type Control,
  type FieldValues,
  type Path,
} from "react-hook-form";
import { DynamicFormRequiredLabel } from "./DynamicFormRequiredLabel";
import { DynamicFormErrorTooltip } from "./DynamicFormErrorTooltip";

type Props<T extends FieldValues> = {
  options: AnswerComponent[];
  control: Control<T, unknown, T>;
  name: Path<T>;
  required?: boolean;
  onAnswer?: () => void;
};

export const DynamicFormMultiSelect = <T extends FieldValues>(
  props: Props<T>,
) => {
  return (
    <Box sx={{ mt: 1 }}>
      <Controller
        control={props.control}
        name={props.name}
        rules={{
          required: props.required ? "This field is required" : false,
        }}
        render={({ field, fieldState }) => {
          return (
            <>
              <DynamicFormRequiredLabel required={props.required} />
              <DynamicFormErrorTooltip fieldState={fieldState} />
              <Box
                sx={(theme) => ({
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  border: fieldState.error
                    ? `1px solid ${theme.palette.error.main}`
                    : `1px solid ${theme.palette.divider}`,
                  padding: 1,
                  borderRadius: 2,
                })}
                ref={field.ref}
                id={field.name}
              >
                {props.options.map((answer) => (
                  <DynamicFormMultiSelectItem
                    key={answer.id}
                    answer={answer}
                    checked={
                      field.value?.some(
                        (val: { id: number; text: string }) =>
                          val.id === answer.id,
                      ) ?? false
                    }
                    disabled={field.disabled}
                    onChange={(id) => {
                      const answerArray: { id: number }[] =
                        field.value?.slice() || [];
                      const index = answerArray.findIndex(
                        (ans) => ans.id === id,
                      );

                      if (index > -1) {
                        answerArray.splice(index, 1);
                      } else {
                        answerArray.push({ id });
                      }

                      field.onChange(answerArray);
                      field.onBlur();
                      props.onAnswer?.();
                    }}
                  />
                ))}
              </Box>
            </>
          );
        }}
      />
    </Box>
  );
};

type DynamicFormMultiSelectItemProps = {
  onChange: (id: number) => void;
  checked: boolean;
  answer: AnswerComponent;
  disabled?: boolean;
  onBlur?: () => void;
};

const DynamicFormMultiSelectItem = (props: DynamicFormMultiSelectItemProps) => {
  const ref = useRef<HTMLDivElement>(null);

  // const getAnswerText = () => {
  //   const element = ref?.current;
  //   if (!element) return "";

  //   return element.textContent || "";
  // };

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: 2,
        border: `1px solid ${
          props.checked ? theme.palette.primary.main : theme.palette.divider
        }`,
        borderRadius: 2,
        padding: 0.5,
        cursor: "pointer",
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
      })}
      onClick={() => props.onChange(props.answer.id)}
      onMouseUp={() => props.onBlur?.()}
    >
      <Box>
        <Checkbox
          onChange={() => props.onChange(props.answer.id)}
          value={props.answer.id}
          checked={props.checked}
          disabled={props.disabled}
        />
      </Box>
      <Box ref={ref}>
        {props.answer.text && (
          <TextBlocksRenderer content={props.answer.text} />
        )}
      </Box>
    </Box>
  );
};
